import { useState } from 'react';
import toast from 'react-hot-toast';
import BottomSheet from '../../../../components/ui/BottomSheet';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';

/**
 * Export Tests bottom sheet — one-tap download rows + email sub-form.
 *
 * Props:
 *   open                 — controlled open state
 *   onClose              — close handler
 *   siteEmail            — site contact email pre-selected as recipient
 *   onExportFullReport   — triggers full-test-results PDF download
 *   onExportDefectReport — triggers defect report PDF download
 *   onSendEmailReport    — (recipientEmail, reportType) => Promise; sends the
 *                          selected report type to the recipient. Errors are
 *                          surfaced by the caller's own toast — see handleSend.
 */
export default function ExportTestsSheet({
  open,
  onClose,
  siteEmail = '',
  onExportFullReport,
  onExportDefectReport,
  onSendEmailReport,
}) {
  // 'session' matches the reportType key buildReportBlob/handleSendEmailReport
  // expect (not the display label "Full Test Results").
  const [reportType, setReportType] = useState('session');
  const [recipient, setRecipient] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    const targetEmail = recipient || siteEmail;
    if (!targetEmail) {
      toast.error('No recipient email available.');
      return;
    }
    setSending(true);
    try {
      await onSendEmailReport?.(targetEmail, reportType);
      onClose();
    } catch {
      // toast already shown by handleSendEmailReport — keep the sheet open so
      // the tech can see the error and retry rather than losing their draft
    } finally {
      setSending(false);
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div className="export-sheet">

        {/* Header — no close X; Cancel is at the bottom */}
        <div className="export-sheet__header">
          <h2 className="export-sheet__title">Export Tests</h2>
          <p className="export-sheet__subtitle">
            Export the testing and defect reports, or email them.
          </p>
        </div>

        {/* One-tap download rows */}
        <div className="export-sheet__download-group">
          <button
            type="button"
            className="export-sheet__download-row"
            onClick={() => { onExportFullReport?.(); onClose(); }}
          >
            Export Full test results report
          </button>
          <button
            type="button"
            className="export-sheet__download-row export-sheet__download-row--last"
            onClick={() => { onExportDefectReport?.(); onClose(); }}
          >
            Export defect report
          </button>
        </div>

        {/* Email sub-form */}
        <p className="export-sheet__email-intro">
          Send the generated PDF directly to a recipient.
        </p>

        <Select
          id="export-report-type"
          label="REPORT TYPE"
          value={reportType}
          onChange={(e) => setReportType(e.target.value)}
          className="export-sheet__select"
        >
          <option value="session">Full Test Results</option>
          <option value="defect">Defect Report</option>
        </Select>

        <Select
          id="export-recipient"
          label="RECIPIENT EMAIL"
          value={recipient || siteEmail}
          onChange={(e) => setRecipient(e.target.value)}
          className="export-sheet__select"
        >
          {siteEmail ? (
            <option value={siteEmail}>{siteEmail} (Site email)</option>
          ) : (
            <option value="">No contacts on file</option>
          )}
        </Select>

        {/* Actions */}
        <div className="export-sheet__actions">
          <Button
            variant="primary"
            className="session-summary-sheet__cta"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Send PDF'}
          </Button>
          <button type="button" className="export-sheet__cancel-btn" onClick={onClose}>
            Cancel
          </button>
        </div>

      </div>
    </BottomSheet>
  );
}
